import { useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const comic = useMemo(() => comics.find((c) => c.id === id), [id]);

  if (!comic) {
    return (
      <div className="w-full px-4 md:px-6 py-20 bg-background-50 text-center">
        <i className="ri-emotion-sad-line text-5xl text-foreground-400 mb-4 block"></i>
        <h1 className="text-xl font-heading text-foreground-900 mb-2">漫画未找到</h1>
        <p className="text-foreground-600 mb-6">抱歉，您查找的漫画不存在或已下架。</p>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          返回首页
        </button>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === comic.category);
  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩漫漫画网`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[0]?.updatedAt || comic.updatedAt,
    dateModified: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '韩漫漫画网',
      url: 'https://www.hanmancomic.com/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.hanmancomic.com/article/${comic.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.hanmancomic.com/' },
      { '@type': 'ListItem', position: 2, name: `${category?.name || ''}漫画`, item: `https://www.hanmancomic.com/category/${comic.category}` },
      { '@type': 'ListItem', position: 3, name: comic.title },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([articleSchema, breadcrumbSchema]),
        }}
      />

      <article className="w-full px-4 md:px-6 py-8 bg-background-50">
        <div className="max-w-7xl mx-auto">
          {/* Comic Header */}
          <div className="flex flex-col md:flex-row gap-8 mb-10">
            <div className="w-full md:w-64 shrink-0">
              <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={comic.cover}
                  alt={`${comic.title} - 韩漫免费在线阅读`}
                  title={`${comic.title} - 韩漫漫画网`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 font-bold mb-3">
                {comic.title}
              </h1>

              <div className="flex items-center gap-3 flex-wrap mb-4">
                <span className="text-sm text-foreground-600">作者：{comic.author}</span>
                <span className="text-foreground-400">|</span>
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    {category.name}
                  </Link>
                )}
                <span className="text-foreground-400">|</span>
                <span className={`text-sm font-medium ${comic.status === '已完结' ? 'text-accent-500' : 'text-primary-500'}`}>
                  {comic.status}
                </span>
              </div>

              <div className="flex items-center gap-4 mb-4 text-sm text-foreground-600">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-secondary-500"></i>
                  <strong>{comic.rating}</strong>
                </span>
                <span>
                  <i className="ri-eye-line mr-1"></i>
                  {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views} 阅读
                </span>
                <span>
                  <i className="ri-heart-line mr-1"></i>
                  {comic.favorites >= 10000 ? `${(comic.favorites / 10000).toFixed(0)}万` : comic.favorites} 收藏
                </span>
              </div>

              <div className="flex items-center gap-2 flex-wrap mb-4">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 text-foreground-700 text-xs rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <p className="text-sm text-foreground-700 leading-relaxed mb-6">
                {comic.description}
              </p>

              <div className="flex items-center gap-3">
                <button className="px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  开始阅读
                </button>
                <button className="px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5">
                  <i className="ri-heart-line"></i>
                  收藏
                </button>
              </div>
            </div>
          </div>

          {/* Chapter List */}
          <section className="mb-10">
            <h2 className="text-lg font-heading text-foreground-950 font-bold mb-4 flex items-center gap-2">
              <i className="ri-list-check text-primary-500"></i>
              章节列表（共 {comic.chapters.length} 话）
            </h2>
            <div className="bg-background-100 rounded-lg p-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 max-h-96 overflow-y-auto">
                {comic.chapters.map((ch) => (
                  <button
                    key={ch.id}
                    className="w-full text-left px-4 py-2.5 rounded-lg bg-background-50 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {ch.title}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Related Comics */}
          {relatedComics.length > 0 && (
            <section>
              <h2 className="text-lg font-heading text-foreground-950 font-bold mb-4 flex items-center gap-2">
                <i className="ri-link-m text-accent-500"></i>
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {relatedComics.map((rc) => (
                  <Link
                    key={rc.id}
                    to={`/article/${rc.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                      <img
                        src={rc.cover}
                        alt={`${rc.title} - 韩漫免费在线阅读`}
                        title={`${rc.title} - 韩漫漫画网`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                      <div className="absolute top-2 left-2 px-2 py-0.5 bg-primary-500 text-white text-xs rounded-full font-medium">
                        {rc.rating}
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {rc.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">{rc.author}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
    </>
  );
}